import type { RoleProps } from "contentful-management";

import {
  contentTypeIds,
  ContentTypeId,
  LocaleTag,
  topLevelContentIds,
} from "../types";
import { Locale } from "../locale";

export const allowCreateForContentType = (
  entryId: ContentTypeId,
): RoleProps["policies"][number] => {
  return {
    effect: "allow",
    constraint: {
      and: [
        {
          equals: [
            {
              doc: "sys.type",
            },
            "Entry",
          ],
        },
        {
          equals: [
            {
              doc: "sys.contentType.sys.id",
            },

            entryId,
          ],
        },
      ],
    },
    actions: ["create"],
  };
};

export const denyPublishUnpublishForAllContent =
  (): RoleProps["policies"][number] => {
    return {
      effect: "deny",
      constraint: {
        and: [
          {
            equals: [
              {
                doc: "sys.type",
              },
              "Entry",
            ],
          },
        ],
      },

      actions: ["publish", "unpublish"],
    };
  };

export const allowPublishUnpublishForAllContent =
  (): RoleProps["policies"][number] => {
    return {
      effect: "allow",
      constraint: {
        and: [
          {
            equals: [
              {
                doc: "sys.type",
              },
              "Entry",
            ],
          },
        ],
      },

      actions: ["publish", "unpublish"],
    };
  };

export const allowPublishUnpublishAndDeleteForAllContent =
  (): RoleProps["policies"][number] => {
    return {
      effect: "allow",
      constraint: {
        and: [
          {
            equals: [
              {
                doc: "sys.type",
              },
              "Entry",
            ],
          },
        ],
      },

      actions: ["publish", "unpublish", "delete"],
    };
  };

export const allowUpdateForAllContent = (): RoleProps["policies"][number] => {
  return {
    effect: "allow",
    constraint: {
      and: [
        {
          equals: [
            {
              doc: "sys.type",
            },
            "Entry",
          ],
        },
      ],
    },
    actions: ["update"],
  };
};

export const allowUpdateForOwnContentOfType = (
  entryId: ContentTypeId,
): RoleProps["policies"][number] => {
  return {
    effect: "allow",
    constraint: {
      and: [
        {
          equals: [
            {
              doc: "sys.type",
            },
            "Entry",
          ],
        },
        {
          equals: [
            {
              doc: "sys.createdBy.sys.id",
            },
            "User.current()",
          ],
        },
        {
          equals: [
            {
              doc: "sys.contentType.sys.id",
            },
            entryId,
          ],
        },
      ],
    },
    actions: ["update"],
  };
};

export const allowReadForAllContent = (): RoleProps["policies"][number] => {
  return {
    effect: "allow",
    constraint: {
      and: [
        {
          equals: [
            {
              doc: "sys.type",
            },
            "Entry",
          ],
        },
      ],
    },
    actions: ["read"],
  };
};

export const allowReadForContentType = (
  entryId: ContentTypeId,
): RoleProps["policies"][number] => {
  return {
    effect: "allow",
    constraint: {
      and: [
        {
          equals: [
            {
              doc: "sys.type",
            },
            "Entry",
          ],
        },
        {
          equals: [
            {
              doc: "sys.contentType.sys.id",
            },

            entryId,
          ],
        },
      ],
    },
    actions: ["read"],
  };
};

export const allowEditForTag = (
  tag: LocaleTag,
): RoleProps["policies"][number] => {
  return {
    effect: "allow",
    constraint: {
      and: [
        {
          equals: [
            {
              doc: "sys.type",
            },
            "Entry",
          ],
        },
        {
          paths: [
            {
              doc: "fields.%.%",
            },
          ],
        },
        {
          in: [
            {
              doc: "metadata.tags.sys.id",
            },
            [tag],
          ],
        },
      ],
    },
    actions: ["update"],
  };
};

export const allowDeleteForTag = (
  tag: LocaleTag,
): RoleProps["policies"][number] => {
  return {
    effect: "allow",
    constraint: {
      and: [
        {
          equals: [
            {
              doc: "sys.type",
            },
            "Entry",
          ],
        },
        {
          in: [
            {
              doc: "metadata.tags.sys.id",
            },
            [tag],
          ],
        },
      ],
    },
    actions: ["delete"],
  };
};

export const allowPublishUnpublishForTag = (
  tag: LocaleTag,
): RoleProps["policies"][number] => {
  return {
    effect: "allow",
    constraint: {
      and: [
        {
          equals: [
            {
              doc: "sys.type",
            },
            "Entry",
          ],
        },
        {
          in: [
            {
              doc: "metadata.tags.sys.id",
            },
            [tag],
          ],
        },
      ],
    },
    actions: ["publish", "unpublish"],
  };
};

export const allowEditForField = (
  entryId: ContentTypeId,
  fieldSelector: string,
): RoleProps["policies"][number] => {
  return {
    effect: "allow",
    constraint: {
      and: [
        {
          equals: [
            {
              doc: "sys.type",
            },
            "Entry",
          ],
        },
        {
          equals: [
            {
              doc: "sys.contentType.sys.id",
            },
            entryId,
          ],
        },
        {
          paths: [
            {
              doc: fieldSelector,
            },
          ],
        },
      ],
    },
    actions: ["update"],
  };
};

export const allowEverythingExceptPublishForOwnContent =
  (): RoleProps["policies"][number] => {
    return {
      effect: "allow",
      constraint: {
        and: [
          {
            equals: [
              {
                doc: "sys.type",
              },
              "Entry",
            ],
          },
          {
            equals: [
              {
                doc: "sys.createdBy.sys.id",
              },
              "User.current()",
            ],
          },
        ],
      },
      actions: ["read", "update", "create"],
    };
  };

export const allowEverythingForOwnContent =
  (): RoleProps["policies"][number] => {
    return {
      effect: "allow",
      constraint: {
        and: [
          {
            equals: [
              {
                doc: "sys.type",
              },
              "Entry",
            ],
          },
          {
            equals: [
              {
                doc: "sys.createdBy.sys.id",
              },
              "User.current()",
            ],
          },
        ],
      },
      actions: "all",
    };
  };

export const denyMigrationData = (): RoleProps["policies"][number] => {
  return {
    effect: "deny",
    constraint: {
      and: [
        {
          equals: [
            {
              doc: "sys.type",
            },
            "Entry",
          ],
        },
        {
          equals: [
            {
              doc: "sys.contentType.sys.id",
            },
            contentTypeIds.migrationData,
          ],
        },
      ],
    },
    actions: "all",
  };
};

export const assetPermissions = (): RoleProps["policies"] => {
  return [
    {
      effect: "allow",
      constraint: {
        and: [
          {
            equals: [
              {
                doc: "sys.type",
              },
              "Asset",
            ],
          },
        ],
      },
      actions: "all",
    },
    {
      effect: "allow",
      constraint: {
        and: [
          {
            equals: [
              {
                doc: "sys.type",
              },
              "Asset",
            ],
          },
          {
            equals: [
              {
                doc: "sys.createdBy.sys.id",
              },
              "User.current()",
            ],
          },
        ],
      },
      actions: "all",
    },
    {
      effect: "allow",
      constraint: {
        and: [
          {
            equals: [
              {
                doc: "sys.type",
              },
              "Asset",
            ],
          },
        ],
      },
      actions: ["create"],
    },
    {
      effect: "allow",
      constraint: {
        and: [
          {
            equals: [
              {
                doc: "sys.type",
              },
              "Asset",
            ],
          },
        ],
      },
      actions: ["read"],
    },
    {
      effect: "allow",
      constraint: {
        and: [
          {
            equals: [
              {
                doc: "sys.type",
              },
              "Asset",
            ],
          },
          {
            equals: [
              {
                doc: "sys.createdBy.sys.id",
              },
              "User.current()",
            ],
          },
        ],
      },
      actions: ["publish", "unpublish"],
    },
    {
      effect: "deny",
      constraint: {
        and: [
          {
            equals: [
              {
                doc: "sys.type",
              },
              "Asset",
            ],
          },
        ],
      },
      actions: ["archive", "unarchive"],
    },
  ];
};

export const allowPublishUnpublishForContentType = (
  entryId: ContentTypeId,
): RoleProps["policies"][number] => {
  return {
    effect: "allow",
    constraint: {
      and: [
        {
          equals: [
            {
              doc: "sys.type",
            },
            "Entry",
          ],
        },
        {
          equals: [
            {
              doc: "sys.contentType.sys.id",
            },
            entryId,
          ],
        },
      ],
    },
    actions: ["publish", "unpublish"],
  };
};

export const allowPublishUnpublishForOwnContent = (
  entryId: ContentTypeId,
): RoleProps["policies"][number] => {
  return {
    effect: "allow",
    constraint: {
      and: [
        {
          equals: [
            {
              doc: "sys.type",
            },
            "Entry",
          ],
        },
        {
          equals: [
            {
              doc: "sys.createdBy.sys.id",
            },
            "User.current()",
          ],
        },
        {
          equals: [
            {
              doc: "sys.contentType.sys.id",
            },

            entryId,
          ],
        },
      ],
    },
    actions: ["publish", "unpublish"],
  };
};

export const allowDeleteForOwnContent = (): RoleProps["policies"][number] => {
  return {
    effect: "allow",
    constraint: {
      and: [
        {
          equals: [
            {
              doc: "sys.type",
            },
            "Entry",
          ],
        },
        {
          equals: [
            {
              doc: "sys.createdBy.sys.id",
            },
            "User.current()",
          ],
        },
      ],
    },
    actions: ["delete"],
  };
};

export const allowLocalizationEditing = (
  entryId: ContentTypeId,
): RoleProps["policies"][number] => {
  return {
    effect: "allow",
    constraint: {
      and: [
        {
          equals: [
            {
              doc: "sys.type",
            },
            "Entry",
          ],
        },
        {
          equals: [
            {
              doc: "sys.contentType.sys.id",
            },
            entryId,
          ],
        },
        {
          paths: [
            {
              doc: "fields.localized.%",
            },
          ],
        },
      ],
    },
    actions: ["update"],
  };
};

export const denyPhraseTranslationSubmission = (): RoleProps["policies"] => {
  return topLevelContentIds.map((id) => ({
    effect: "deny",
    constraint: {
      and: [
        {
          equals: [
            {
              doc: "sys.type",
            },
            "Entry",
          ],
        },
        {
          equals: [
            {
              doc: "sys.contentType.sys.id",
            },
            id,
          ],
        },
        {
          paths: [
            {
              doc: "fields.translationData",
            },
          ],
        },
      ],
    },
    actions: ["update"],
  }));
};

export const denyEditForOtherLocales = (
  enabledLocales: Locale[],
  allowedLocale: Locale,
): RoleProps["policies"] => {
  return topLevelContentIds
    .map((contentTypeId) =>
      enabledLocales
        .filter((localeTag) => localeTag !== allowedLocale)
        .map((deniedLocale) => ({
          effect: "deny",
          actions: ["update" as const],
          constraint: {
            and: [
              {
                equals: [
                  {
                    doc: "sys.type",
                  },
                  "Entry",
                ],
              },
              {
                equals: [
                  {
                    doc: "sys.contentType.sys.id",
                  },
                  contentTypeId,
                ],
              },
              {
                paths: [
                  {
                    doc: `fields.localized.${deniedLocale}`,
                  },
                ],
              },
            ],
          },
        })),
    )
    .flat();
};
